import React from 'react'
import { motion } from 'framer-motion'
import { Star, Quote } from 'lucide-react'

const Testimonials = () => {
  const testimonials = [
    {
      id: 1,
      name: "María González",
      location: "Cali, Colombia",
      rating: 5,
      text: "¡No puedo creer que me gané un viaje a París! El proceso fue súper fácil y transparente. Definitivamente seguiré participando en más membresías.",
      prize: "Viaje a París",
      avatar: "👩🏻"
    },
    {
      id: 2,
      name: "Carlos Rodríguez",
      location: "Bogotá, Colombia",
      rating: 5,
      text: "Llevo 6 meses participando y ya he ganado dos veces. El sistema de números únicos me da mucha confianza. ¡Me gané un viaje a Japón increíble!",
      prize: "Viaje a Japón",
      avatar: "👨🏽"
    },
    {
      id: 3,
      name: "Ana Martínez",
      location: "Medellín, Colombia",
      rating: 5,
      text: "La plataforma es increíblemente fácil de usar. Compré mis boletos en segundos y el pago fue súper seguro. ¡Me gané un viaje a Italia!",
      prize: "Viaje a Italia",
      avatar: "👩🏼"
    },
    {
      id: 4,
      name: "David López",
      location: "Cartagena, Colombia",
      rating: 5,
      text: "Al principio era escéptico, pero después de ganar el viaje a Nueva York me convertí en un fan. La transparencia del proceso es impresionante.",
      prize: "Viaje a Nueva York",
      avatar: "👨🏻"
    },
    {
      id: 5,
      name: "Laura Fernández",
      location: "Cali, Colombia",
      rating: 5,
      text: "He participado en muchas membresías online, pero esta es la única que realmente cumple lo que promete. ¡Me gané un viaje a Grecia espectacular!",
      prize: "Viaje a Grecia",
      avatar: "👩🏾"
    },
    {
      id: 6,
      name: "Miguel Torres",
      location: "Bogotá, Colombia",
      rating: 5,
      text: "La atención al cliente es excelente y las membresías son completamente legítimas. Mi viaje a Dubai fue exactamente como prometieron.",
      prize: "Viaje a Dubai",
      avatar: "👨🏼"
    }
  ]

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-400'}`}
      />
    ))
  }

  return (
    <section id="testimonios" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Lo que dicen nuestros
            <span className="gradient-text block">Ganadores</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Miles de personas ya han ganado increíbles premios. Lee sus experiencias reales
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="card-modern p-6 group cursor-pointer relative"
            >
              {/* Quote Icon */}
              <div className="absolute top-4 right-4 text-primary-400/20">
                <Quote className="h-8 w-8" />
              </div>

              <div className="space-y-4">
                {/* User Info */}
                <div className="flex items-center space-x-4">
                  <div className="text-4xl">{testimonial.avatar}</div>
                  <div>
                    <h4 className="font-semibold text-white">{testimonial.name}</h4>
                    <p className="text-sm text-gray-400">{testimonial.location}</p>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-1">
                  {renderStars(testimonial.rating)}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-300 leading-relaxed italic">
                  "{testimonial.text}"
                </p>

                {/* Prize Won */}
                <div className="pt-4 border-t border-gray-700">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-400">Premio ganado:</span>
                    <span className="text-sm font-semibold text-primary-400">
                      {testimonial.prize}
                    </span>
                  </div>
                </div>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-secondary-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-16 card-modern p-8"
        >
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-white mb-2">5,247</div>
              <div className="text-gray-400">Ganadores Felices</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">$2.1M</div>
              <div className="text-gray-400">En Premios Entregados</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">4.9/5</div>
              <div className="text-gray-400">Calificación Promedio</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">24h</div>
              <div className="text-gray-400">Entrega de Premios</div>
            </div>
          </div>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">✓</span>
              </div>
              <span className="text-gray-300">Verificado por TrustPilot</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">🔒</span>
              </div>
              <span className="text-gray-300">SSL Certificado</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">⚡</span>
              </div>
              <span className="text-gray-300">Pagos Instantáneos</span>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <h3 className="text-2xl font-bold text-white mb-4">
            ¿Listo para ser el próximo ganador?
          </h3>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-primary text-lg px-8 py-4"
          >
            Participar Ahora
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}

export default Testimonials